import React, { useEffect, useRef } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { analytics } from 'moet-core';
import { isRunningContextCypress } from 'moet-test-utils/cypress/utils';

import { FEATURES } from '@/constants';
import { setRecaptchaToken, setRecaptchaTokenExpired, skipRecaptcha } from '@/actions/recaptcha';
import { shouldExecute } from '@/selectors/recaptcha';
import { getFeatureByKey } from '@/selectors/featuresConfig';

import { ANALYTICS_EVENTS } from './analyticsEvents';
import './ReCaptcha.css';

// changed to GoogleReCaptchaProvider key
const SITEKEY = '6LeLFL4aAAAAANHFOwQiRdNIwXX7B5iVfFolOqW1';

/** keeps recaptcha visible when clicked outside */
const initObserver = () => {
  const recaptchaWindow = [...document.getElementsByTagName('iframe')]?.find(x =>
    x.src.includes('google.com/recaptcha/enterprise'),
  )?.parentNode?.parentNode;

  if (recaptchaWindow) {
    const observer = new MutationObserver(() => {
      // ReCaptcha changes these 3 props when going invisible.
      if (
        recaptchaWindow.style.visibility !== 'visible' ||
        recaptchaWindow.style.opacity !== '1' ||
        recaptchaWindow.style.top !== '10px'
      ) {
        // If changed, put back on default values.
        recaptchaWindow.style.opacity = '1';
        recaptchaWindow.style.visibility = 'visible';
        recaptchaWindow.style.top = '10px';
      }
    });
    observer.observe(recaptchaWindow, {
      attributeFilter: ['style'],
    });

    return observer;
  }
};

/** Handles reCaptcha v2 logic */
export const ReCaptchaComponent = props => {
  const {
    setRecaptchaToken,
    setRecaptchaTokenExpired,
    shouldExecute,
    skipRecaptcha,
    skipRecaptchaOn,
  } = props;
  const ref = useRef(null);

  useEffect(() => {
    if (!shouldExecute) return;

    if (isRunningContextCypress(window) || skipRecaptchaOn) {
      skipRecaptcha();
    } else {
      const observer = initObserver();
      ref.current.execute();
      analytics.logEvent(ANALYTICS_EVENTS.recaptchaShowing);
      return () => observer?.disconnect();
    }
  }, [shouldExecute]);

  const onChange = response => {
    if (response) {
      setRecaptchaToken(response);
      ref.current.reset();
      analytics.logEvent(ANALYTICS_EVENTS.recaptchaSubmitted);
    }
  };

  const onExpired = () => {
    setRecaptchaTokenExpired();
    analytics.logEvent(ANALYTICS_EVENTS.recaptchaExpired);
  };

  return (
    <ReCAPTCHA
      ref={ref}
      sitekey={SITEKEY}
      onChange={onChange}
      onExpired={onExpired}
      size="invisible"
    />
  );
};

ReCaptchaComponent.propTypes = {
  setRecaptchaToken: PropTypes.func.isRequired,
  setRecaptchaTokenExpired: PropTypes.func.isRequired,
  shouldExecute: PropTypes.bool.isRequired,
  skipRecaptcha: PropTypes.func.isRequired,
  skipRecaptchaOn: PropTypes.bool.isRequired,
};

export const ReCaptcha = compose(
  connect(
    state => ({
      shouldExecute: shouldExecute(state),
      skipRecaptchaOn: getFeatureByKey(FEATURES.SKIP_RECAPTCHA_CHALLENGE)(state),
    }),
    {
      setRecaptchaToken,
      setRecaptchaTokenExpired,
      skipRecaptcha,
    },
  ),
)(ReCaptchaComponent);
